// Botón genérico para exportar cualquier lista del admin.
// 3 modos: Descargar CSV, Copiar TSV (pegar directo en Sheets), Abrir Sheets nuevo + descargar.
//
// Uso:
//   <ExportButton
//     data={porteros}
//     columns={[
//       { key: 'profile.nombre', label: 'Nombre' },
//       { key: 'edificio_nombre', label: 'Edificio' },
//       { key: 'refs.total', label: 'Vecinos' },
//     ]}
//     filename="porteros"  // sin extensión, se agrega fecha automática
//   />

import { useEffect, useRef, useState } from 'react'
import { toCsv, toTsv, downloadCsv, copyToClipboard } from '../../lib/exportToCsv'

const NAVY = '#1A3461'

export default function ExportButton({ data, columns, filename = 'export', label = 'Exportar', disabled = false }) {
  const [open, setOpen] = useState(false)
  const [msg, setMsg] = useState(null)
  const ref = useRef(null)

  // Cierra el menu al click fuera
  useEffect(() => {
    if (!open) return
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const fecha = new Date().toISOString().slice(0, 10)
  const fname = `limpack-${filename}-${fecha}`
  const count = (data || []).length

  function flash(text) {
    setMsg(text); setTimeout(() => setMsg(null), 1800)
  }

  function handleDownload() {
    setOpen(false)
    downloadCsv(toCsv(data, columns), fname)
    flash(`Descargado · ${count} filas`)
  }

  async function handleCopy() {
    setOpen(false)
    const ok = await copyToClipboard(toTsv(data, columns))
    if (ok) flash(`Copiado · ${count} filas · Pegá en Sheets`)
    else flash('No pude copiar — descargá el CSV')
  }

  function handleOpenSheets() {
    setOpen(false)
    downloadCsv(toCsv(data, columns), fname)
    setTimeout(() => window.open('https://sheet.new', '_blank'), 100)
    flash('Descargado CSV · Importá en la Sheet nueva')
  }

  return (
    <div ref={ref} style={{ position:'relative', display:'inline-block' }}>
      <button
        onClick={() => setOpen(o => !o)}
        disabled={disabled || count === 0}
        title={count === 0 ? 'Sin datos para exportar' : `Exportar ${count} filas`}
        style={{
          fontSize: 13, padding:'7px 12px',
          border: `0.5px solid ${disabled || count === 0 ? '#CBD5E1' : NAVY}`,
          borderRadius: 6, background:'#fff',
          color: disabled || count === 0 ? '#94A3B8' : NAVY,
          fontWeight: 600, cursor: disabled || count === 0 ? 'not-allowed' : 'pointer',
          fontFamily:'inherit',
          display:'inline-flex', alignItems:'center', gap: 6,
        }}
      >
        ⬇️ {label}
        <span style={{ fontSize: 10, marginLeft: 2 }}>▾</span>
      </button>

      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 4px)', right: 0,
          background:'#fff', border:'0.5px solid #E2E8F0', borderRadius: 8,
          boxShadow:'0 4px 14px rgba(0,0,0,0.08)', zIndex: 50,
          minWidth: 240, padding: 4,
        }}>
          <MenuItem icon="⬇️" title="Descargar CSV" desc="Abre en Excel / Numbers / Sheets" onClick={handleDownload} />
          <MenuItem icon="📋" title="Copiar al portapapeles" desc="Pegá directo en una Sheet abierta" onClick={handleCopy} />
          <MenuItem icon="🆕" title="Abrir Sheets nuevo" desc="Crea Sheet en blanco + descarga CSV" onClick={handleOpenSheets} />
          <div style={{ padding:'6px 10px', fontSize: 11, color:'#94A3B8', borderTop:'0.5px solid #F1F5F9', marginTop: 4 }}>
            {count} fila{count !== 1 ? 's' : ''} · {columns.length} columna{columns.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {msg && (
        <div style={{
          position:'absolute', top:'calc(100% + 4px)', right: 0,
          background: NAVY, color:'#fff', fontSize: 12, fontWeight: 600,
          padding:'6px 10px', borderRadius: 6, zIndex: 60, whiteSpace:'nowrap',
          boxShadow:'0 4px 14px rgba(0,0,0,0.15)',
        }}>
          {msg}
        </div>
      )}
    </div>
  )
}

function MenuItem({ icon, title, desc, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display:'flex', alignItems:'flex-start', gap: 10,
        width:'100%', padding:'8px 10px',
        background:'transparent', border:'none', borderRadius: 4,
        textAlign:'left', cursor:'pointer', fontFamily:'inherit',
      }}
      onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <span style={{ fontSize: 16, lineHeight: 1.2, marginTop: 1 }}>{icon}</span>
      <span style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: NAVY }}>{title}</div>
        <div style={{ fontSize: 11, color:'#64748B', marginTop: 1 }}>{desc}</div>
      </span>
    </button>
  )
}
